import React, { useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, Search } from "lucide-react";

import { DashboardLayout } from "@/components/layout";
import {
  Button,
  Card,
  ConfirmationModal,
  PageHeader,
  showToast,
} from "@/components/ui";
import { cn } from "@/lib/utils";
import { statusToWorkspaceStatus } from "@/components/sellers/seller.constants";
import { SellersKpiCards } from "@/components/sellers/SellersKpiCards";
import { SellersTableRow } from "@/components/sellers/SellersTableRow";
import { useAdminDashboard } from "@/hooks/useAdminDashboard";
import {
  setSellerStatus,
  useSellers,
  type SellerListItem,
} from "@/hooks/useSellers";

const PAGE_SIZE = 10;

export default function Sellers() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [page, setPage] = useState(1);

  const { data: dash, loading: dashLoading } = useAdminDashboard();

  const {
    data: sellersData,
    loading: sellersLoading,
    refetch,
  } = useSellers({
    search: search || undefined,
    status: statusToWorkspaceStatus[statusFilter] || undefined,
    page,
    pageSize: PAGE_SIZE,
  });

  const [pendingId, setPendingId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<SellerListItem | null>(null);
  const blocking = confirming?.status === "ACTIVE";

  async function toggleStatus(seller: SellerListItem) {
    const block = seller.status === "ACTIVE";
    const verb = block ? "Block" : "Unblock";
    setConfirming(null);
    setPendingId(seller.id);
    try {
      await setSellerStatus(seller.id, block ? "SUSPENDED" : "ACTIVE");
      await refetch();
      showToast.success(
        block ? `${seller.name} is blocked` : `${seller.name} is unblocked`,
      );
    } catch (err) {
      showToast.error(
        `${verb} failed`,
        err instanceof Error ? err.message : "Unknown error",
      );
    } finally {
      setPendingId(null);
    }
  }

  const items = sellersData?.items ?? [];
  const totalPages = sellersData?.totalPages ?? 1;
  const totalCount = sellersData?.totalCount ?? 0;

  return (
    <DashboardLayout activePath="/sellers">
      <PageHeader
        title="Sellers"
        subtitle="Monitor seller performance, activity, and operational health."
        breadcrumbs={[{ label: "Sellers", active: true }]}
      />

      <SellersKpiCards dash={dash} dashLoading={dashLoading} />

      <div
        data-testid="sellers-filters"
        className="flex flex-wrap items-center gap-3 mb-6"
      >
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            data-testid="sellers-search"
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search by store name / owner name / email / phone / store ID"
            className={cn(
              "w-full pl-10 pr-4 py-2 text-sm rounded-lg",
              "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700",
              "text-slate-900 dark:text-slate-100 placeholder:text-slate-400",
              "focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500",
            )}
          />
        </div>
        <div className="relative">
          <select
            data-testid="status-filter"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className={cn(
              "appearance-none pl-3 pr-8 py-2 text-sm rounded-lg",
              "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700",
              "text-slate-900 dark:text-slate-100",
              "focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500",
            )}
          >
            {["All", "Active", "Suspended", "Closed"].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        </div>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          {sellersLoading ? "…" : `${totalCount} sellers`}
        </span>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table data-testid="sellers-table" className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                {[
                  "Seller",
                  "Workspace",
                  "Plan",
                  "Products",
                  "Orders",
                  "Revenue (30d)",
                  "Inventory",
                  "Sync",
                  "Status",
                  "",
                ].map((h, i) => (
                  <th
                    key={i}
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {sellersLoading ? (
                <tr>
                  <td
                    colSpan={10}
                    className="px-4 py-12 text-center text-slate-400 dark:text-slate-500"
                  >
                    Loading…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td
                    colSpan={10}
                    className="px-4 py-12 text-center text-slate-400 dark:text-slate-500"
                  >
                    No sellers match your filters
                  </td>
                </tr>
              ) : (
                items.map((s) => (
                  <SellersTableRow
                    key={s.id}
                    s={s}
                    pending={pendingId === s.id}
                    onToggle={setConfirming}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-700">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1 || sellersLoading}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page === totalPages || sellersLoading}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      <ConfirmationModal
        open={confirming !== null}
        onOpenChange={(open) => !open && setConfirming(null)}
        variant={blocking ? "danger" : "primary"}
        eyebrow={confirming?.ownerEmail ?? undefined}
        title={`${blocking ? "Block" : "Unblock"} ${confirming?.name ?? ""}?`}
        description={
          blocking
            ? "They will be signed out and unable to sign in, and their marketplace syncs will stop until you unblock them."
            : "They will be able to sign in again and their marketplace syncs will resume."
        }
        confirmLabel={blocking ? "Block seller" : "Unblock seller"}
        onConfirm={() => confirming && void toggleStatus(confirming)}
      />
    </DashboardLayout>
  );
}
